import { useEffect, useState } from "react";
import { Default } from "../../components/default/Default";
import { showToaster, handleCopy } from "../../common/common";
import PostRequest from "../../services/PostRequest";
import { McaModal } from "../../components/mca";
import { expireOptions, timeZoneOptions } from "../../data/constants";
import moment from "moment";
import "./profile.css";

const Eye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="2.6" /></svg>
);
const EyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18" /><path d="M10.6 10.6a2.6 2.6 0 0 0 3.6 3.6" /><path d="M9.4 5.2A10 10 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-3.2 4M6.2 6.2A18 18 0 0 0 2 12s3.5 7 10 7a10 10 0 0 0 3-.4" /></svg>
);
const Copy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
);

const SwitchRow = ({ name, label, desc, checked }) => (
  <div className="prof-switch">
    <div>
      <div className="prof-switch__label">{label}</div>
      <div className="prof-switch__desc">{desc}</div>
    </div>
    <label className="mca-switch">
      <input type="checkbox" name={name} defaultChecked={checked} />
      <span className="mca-switch__track" />
    </label>
  </div>
);

// Controlled switch — saves immediately via the parent's onChange (used for the
// notification-preference rows, which persist per toggle through /comms).
const ControlledSwitch = ({ label, desc, checked, disabled, onChange }) => (
  <div className="prof-switch">
    <div>
      <div className="prof-switch__label">{label}</div>
      <div className="prof-switch__desc">{desc}</div>
    </div>
    <label className="mca-switch">
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="mca-switch__track" />
    </label>
  </div>
);

// Confirm + perform an API-key regeneration (rotates UserAccount.api_key in the
// backend via /users/api-key/update/). Mirrors the modal on /account.
const RegenerateKeyModal = ({ open, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const handleRegenerate = async () => {
    try {
      setIsLoading(true);
      const response = await PostRequest("/users/api-key/update/", {}, { showLoader: "key_id" });
      showToaster(response);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      showToaster(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <McaModal
      open={open}
      onClose={onClose}
      title="Regenerate API key"
      footer={
        <>
          <button className="mca-btn mca-btn--outline" onClick={onClose} type="button">Cancel</button>
          <button className="mca-btn mca-btn--primary" id="key_id" onClick={handleRegenerate} disabled={isLoading} type="button">
            {isLoading ? "Regenerating..." : "Yes, regenerate"}
          </button>
        </>
      }
    >
      <div className="mca-alert mca-alert--info">
        <svg className="lead" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" />
        </svg>
        <div className="mca-alert__body">
          <strong>Regenerating immediately invalidates your current key.</strong> Any
          integrations using the old key will stop working — update them with the new key right away.
        </div>
      </div>
    </McaModal>
  );
};

export const Profile = () => {
  const [errors, setErrors] = useState({});
  const [userProfile, setUserProfile] = useState({});
  const [accountDetails, setAccountDetails] = useState({});
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState([]);
  const [notifSaving, setNotifSaving] = useState(null);

  // ---- handlers (unchanged from the original — same endpoints/payloads) ----
  const handelAboutUserUpdate = async (e) => {
    try {
      e.preventDefault();
      const data = {};
      new FormData(e.target).forEach((value, key) => (data[key] = value));
      const newErrors = {};
      if (!data.name) newErrors.name = "Name is required!";
      if (!data.email) newErrors.email = "Email is required!";
      else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email))
        newErrors.email = "Email is invalid!";
      setErrors(newErrors);
      if (Object.keys(newErrors).length) return;
      await PostRequest("/users/profile/update", data, { showLoader: "about-you-id" });
      showToaster("Your profile updated successfully!", "success");
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const handelUpdateDetails = async (e) => {
    try {
      e.preventDefault();
      const data = Object.fromEntries(new FormData(e.target).entries());
      const newErrors = {};
      if (!data.unused_collection_expired)
        newErrors.unused = "Completed Collections Expire After is required!";
      if (!data.timezone) newErrors.timezone = "Timezone is required!";
      setErrors(newErrors);
      if (Object.keys(newErrors).length) return;
      await PostRequest("/users/profile/update", data, { showLoader: "details-id" });
      showToaster("updated successfully.", "success");
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const handleChangePassword = async (e) => {
    try {
      e.preventDefault();
      const data = Object.fromEntries(new FormData(e.target).entries());
      const newErrors = {};
      if (!data.old_password) newErrors.old_password = "Current Password is required!";
      if (!data.new_password) newErrors.new_password = "New Password is required!";
      else if (data.new_password.length < 8)
        newErrors.new_password = "New Password must be at least 8 characters long.";
      setErrors(newErrors);
      if (Object.keys(newErrors).length) return;
      await PostRequest("/users/password/change", data, { showLoader: "changPassword-id" });
      e.target.reset();
      showToaster("Password has been updated.", "success");
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const loadProfile = async () => {
    try { setUserProfile(await PostRequest("/users/profile")); } catch (e) {}
  };
  const loadAccountDetails = async () => {
    try { setAccountDetails(await PostRequest("/users/account-details")); } catch (e) {}
  };
  const loadNotifPrefs = async () => {
    try { setNotifPrefs(await PostRequest("/comms/preferences/")); } catch (e) {}
  };
  useEffect(() => { loadProfile(); loadAccountDetails(); loadNotifPrefs(); }, []);

  // Persist a single category toggle immediately (optimistic; revert on failure).
  const toggleNotif = async (category, enabled) => {
    setNotifPrefs((prev) => prev.map((p) => (p.category === category ? { ...p, enabled } : p)));
    try {
      setNotifSaving(category);
      const fresh = await PostRequest("/comms/preferences/update/", { category, enabled });
      if (Array.isArray(fresh)) setNotifPrefs(fresh);
      showToaster("Notification preferences updated.", "success");
    } catch (error) {
      setNotifPrefs((prev) => prev.map((p) => (p.category === category ? { ...p, enabled: !enabled } : p)));
      showToaster(error.message, "error");
    } finally {
      setNotifSaving(null);
    }
  };

  return (
    <Default>
      <div className="mca-content">
        <div className="mca-page-head">
          <h1>Profile</h1>
          <p>Manage your account, security, and preferences.</p>
        </div>

        <div className="prof-grid">
          <div className="prof-col">
          {/* About you */}
          <form
            className="mca-card"
            onSubmit={handelAboutUserUpdate}
            key={`about-${userProfile?.id || ""}-${accountDetails?.id || ""}`}
          >
            <div className="mca-card__head"><div className="mca-card__title">About you</div></div>
            <div className="mca-card__body">
              <div className="mca-field">
                <label className="mca-label">Name</label>
                <input className="mca-input" name="name" defaultValue={userProfile?.name} placeholder="Your name" required />
                {errors.name && <span className="mca-error">{errors.name}</span>}
              </div>
              <div className="mca-field">
                <label className="mca-label">Email</label>
                <input className="mca-input" type="email" name="email" defaultValue={userProfile?.email} placeholder="you@example.com" required />
                {errors.email && <span className="mca-error">{errors.email}</span>}
              </div>
              <div className="mca-field">
                <label className="mca-label">API key</label>
                <div className="prof-key">
                  <code className="prof-key__val">{apiKeyRevealed ? (accountDetails?.api_key || "—") : "sk_live_••••••••••••••••"}</code>
                  <button type="button" className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm" title={apiKeyRevealed ? "Hide" : "Reveal"} onClick={() => setApiKeyRevealed(!apiKeyRevealed)}>{apiKeyRevealed ? <EyeOff /> : <Eye />}</button>
                  <button type="button" className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm" title="Copy" onClick={() => handleCopy(accountDetails?.api_key || "")}><Copy /></button>
                  <button type="button" className="mca-btn mca-btn--ghost mca-btn--sm" onClick={() => setRegenOpen(true)}>Regenerate</button>
                </div>
              </div>
              <div className="mca-field">
                <label className="mca-label">Joined</label>
                <div className="cell-muted">{userProfile?.created_at ? moment(userProfile.created_at).format("DD MMM YYYY") : "—"}</div>
              </div>
            </div>
            <div className="mca-card__foot">
              <button id="about-you-id" className="mca-btn mca-btn--primary" type="submit">Save changes</button>
            </div>
          </form>

          {/* Notifications — gateable email categories (saved on toggle). Billing
              receipts and security codes are always sent and aren't listed. */}
          <div className="mca-card">
            <div className="mca-card__head"><div className="mca-card__title">Notifications</div></div>
            <div className="mca-card__body">
              {notifPrefs.length === 0 ? (
                <div className="prof-switch__desc">No optional email notifications available.</div>
              ) : (
                notifPrefs.map((p) => (
                  <ControlledSwitch
                    key={p.category}
                    label={p.label}
                    desc={p.description}
                    checked={p.enabled}
                    disabled={notifSaving === p.category}
                    onChange={(val) => toggleNotif(p.category, val)}
                  />
                ))
              )}
              <div className="prof-switch__desc" style={{ marginTop: 10 }}>
                Payment receipts, plan changes and security codes are always sent.
              </div>
            </div>
          </div>
          </div>

          <div className="prof-col">
            {/* Preferences */}
            <form className="mca-card" onSubmit={handelUpdateDetails} key={`det-${accountDetails?.id || ""}`}>
              <div className="mca-card__head"><div className="mca-card__title">Preferences</div></div>
              <div className="mca-card__body">
                <div className="mca-field">
                  <label className="mca-label">Completed collections expire after</label>
                  <select className="mca-select" name="unused_collection_expired" defaultValue={accountDetails?.unused_collection_expired || ""} required>
                    <option value="" disabled>Select…</option>
                    {(expireOptions || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {errors.unused && <span className="mca-error">{errors.unused}</span>}
                </div>
                <div className="mca-field">
                  <label className="mca-label">Timezone</label>
                  <select className="mca-select" name="timezone" defaultValue={accountDetails?.timezone || ""} required>
                    <option value="" disabled>Select…</option>
                    {(timeZoneOptions || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {errors.timezone && <span className="mca-error">{errors.timezone}</span>}
                </div>
              </div>
              <div className="mca-card__foot">
                <button id="details-id" className="mca-btn mca-btn--primary" type="submit">Save</button>
              </div>
            </form>

            {/* Security */}
            <form className="mca-card" onSubmit={handleChangePassword}>
              <div className="mca-card__head"><div className="mca-card__title">Security</div></div>
              <div className="mca-card__body">
                <div className="mca-field">
                  <label className="mca-label">Current password</label>
                  <div className="prof-pw">
                    <input className="mca-input" type={showCur ? "text" : "password"} name="old_password" placeholder="Current password" required />
                    <button type="button" className="prof-pw__toggle" onClick={() => setShowCur(!showCur)} aria-label="Toggle password">{showCur ? <EyeOff /> : <Eye />}</button>
                  </div>
                  {errors.old_password && <span className="mca-error">{errors.old_password}</span>}
                </div>
                <div className="mca-field">
                  <label className="mca-label">New password</label>
                  <div className="prof-pw">
                    <input className="mca-input" type={showNew ? "text" : "password"} name="new_password" placeholder="At least 8 characters" required />
                    <button type="button" className="prof-pw__toggle" onClick={() => setShowNew(!showNew)} aria-label="Toggle password">{showNew ? <EyeOff /> : <Eye />}</button>
                  </div>
                  {errors.new_password && <span className="mca-error">{errors.new_password}</span>}
                </div>
              </div>
              <div className="mca-card__foot">
                <button id="changPassword-id" className="mca-btn mca-btn--primary" type="submit">Change password</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <RegenerateKeyModal
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        onSuccess={loadAccountDetails}
      />
    </Default>
  );
};
